import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Segmented, Space, Spin, Tag, Typography } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TransportRouteSummaryDto } from '../../../dto/transport';
import {
  listTransportRoutesForSelection,
  type TransportAttachResult,
} from '../api/transportRoutesApi';
import { useAttachToTransport } from '../useAttachToTransport';
import {
  deriveTransportKind,
  TRANSPORT_KINDS,
  transportKindLabelKey,
  type TransportKind,
} from '../transportDirection';
import { placeLabel, samePlace } from '../transportKindAddresses';
import { fetchOrderTransports, fetchSupplierOrderCargoContext } from '../../orders/api/orderTransportApi';
import { buildTransportRouteLabel } from '../../orders/transportAddressUtils';
import { getTransportLabel } from '../../../utils/transportLabel';
import { formatDate } from '../../../utils/dateUtils';
import TransportKindTag from './TransportKindTag';
import './TransportAttachCandidates.css';

const { Text } = Typography;
const CLOSED_STATUSES = new Set(['DELIVERED', 'CANCELLED']);
// Already on the road: adding goods would change a loaded truck.
const ON_THE_ROAD_STATUSES = new Set(['IN_TRANSIT', 'DELAYED']);

export type TransportAttachTarget =
  | { type: 'order'; id: string; label: string; place?: string | null }
  | { type: 'supplierOrder'; id: string; label: string; place?: string | null };

interface TransportAttachCandidatesProps {
  target: TransportAttachTarget;
  canViewCosts: boolean;
  onAttached: (result: TransportAttachResult) => void;
  /** Optional „Edytuj ładunek” path (the full form) for the selected truck. */
  onEditInstead?: (transportId: string) => void;
}

function truckKind(transport: TransportRouteSummaryDto): TransportKind {
  return deriveTransportKind({
    direction: transport.direction,
    customerOrderCount: transport.routeOrders.length,
    supplierOrderCount: transport.supplierOrders?.length || 0,
  });
}


/**
 * „Dołóż do istniejącego auta” (decision 2026-10-04, F2): pick a saved truck
 * of a matching kind and add the whole rest of this ZK/ZD with one click
 * (POST /transport-routes/:id/orders). The server recalculates the freight;
 * the toast names the new share.
 */
const TransportAttachCandidates: React.FC<TransportAttachCandidatesProps> = ({
  target,
  canViewCosts,
  onAttached,
  onEditInstead,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [kindFilter, setKindFilter] = useState<'ALL' | TransportKind>('ALL');
  const [search, setSearch] = useState('');
  // Usability round 2026-10-05: before any click, say when nothing of this
  // order is left to add (it is all on other trucks).
  // false = something is left; else why nothing is (round 2, U8).
  const [nothingLeft, setNothingLeft] = useState<false | { truck: string | null }>(false);

  useEffect(() => {
    let active = true;
    const check: Promise<false | { truck: string | null }> = target.type === 'order'
      ? fetchOrderTransports(target.id).then((data) => (
        data.productRealization.length > 0
        && data.productRealization.every((row) => !(Number(row.remainingQuantity) > 0))
          ? { truck: null }
          : false
      ))
      // A ZD another truck carries without entered quantities: that truck
      // takes the whole rest (rule 2026-10-01), so nothing is left.
      : fetchSupplierOrderCargoContext(target.id, null).then((context) => {
        const restTruck = context?.otherTrucks.find((truck) => !truck.listed);
        return restTruck ? { truck: restTruck.label } : false;
      });
    check
      .then((value) => { if (active) setNothingLeft(value); })
      .catch(() => { if (active) setNothingLeft(false); });
    return () => { active = false; };
  }, [target.id, target.type]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    listTransportRoutesForSelection()
      .then((routes) => { if (active) setTransports(routes); })
      .catch(() => { if (active) setLoadFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // A ZD rides only on a truck from the factory; a ZK on any kind.
  const matchingKinds = target.type === 'supplierOrder'
    ? TRANSPORT_KINDS.filter((kind) => kind !== 'WAREHOUSE_TO_CUSTOMER')
    : TRANSPORT_KINDS;

  const candidates = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('pl-PL');
    return transports
      .map((transport) => ({ transport, kind: truckKind(transport) }))
      .filter(({ kind }) => matchingKinds.includes(kind))
      .filter(({ kind }) => kindFilter === 'ALL' || kind === kindFilter)
      .filter(({ transport }) => {
        if (!needle) return true;
        return [
          getTransportLabel(transport),
          transport.transportCompany,
          ...transport.loadingAddresses,
          ...transport.deliveryAddresses,
          ...transport.routeOrders.map((row) => row.order?.displayOrderNumber || row.order?.orderNumber || ''),
          ...(transport.supplierOrders || []).map((row) => row.displayOrderNumber || row.orderNumber),
          formatDate(transport.loadingDate),
        ].join(' ').toLocaleLowerCase('pl-PL').includes(needle);
      });
  }, [kindFilter, matchingKinds, search, transports]);

  const disabledReason = (transport: TransportRouteSummaryDto): string | null => {
    const onTruck = target.type === 'order'
      ? transport.routeOrders.some((row) => row.orderId === target.id)
      : (transport.supplierOrders || []).some((row) => row.id === target.id);
    if (onTruck) return t('transport:attach.alreadyOn');
    if (CLOSED_STATUSES.has(transport.status)) return t('transport:attach.closed');
    if (ON_THE_ROAD_STATUSES.has(transport.status)) return t('transport:attach.inTransit');
    if (nothingLeft) {
      return target.type === 'order'
        ? t('transport:attach.nothingLeftOrder')
        : t('transport:attach.nothingLeftSupplierOrderTruck', { truck: nothingLeft.truck });
    }
    return null;
  };

  const newPointMessage = (transport: TransportRouteSummaryDto): string | null => {
    const place = String(target.place || '').trim();
    if (!place) return null;
    const list = target.type === 'order' ? transport.deliveryAddresses : transport.loadingAddresses;
    // Only a really different address is a new stop; the message names its city.
    if (list.some((entry) => samePlace(entry, place))) return null;
    return t(target.type === 'order' ? 'transport:attach.newUnloadingPoint' : 'transport:attach.newLoadingPoint', {
      city: placeLabel(place),
    });
  };

  const selected = transports.find((transport) => transport.id === selectedId);

  const attachToTruck = useAttachToTransport(canViewCosts);
  const saving = attachToTruck.saving;
  const attach = async () => {
    if (!selected) return;
    const result = await attachToTruck.attach(selected.id, target);
    if (result) onAttached(result);
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-testid="transport-attach-candidates">
      <Text>{t(target.type === 'order' ? 'transport:attach.description' : 'transport:attach.descriptionSupplier')}</Text>
      <Segmented
        value={kindFilter}
        onChange={(value) => setKindFilter(value as 'ALL' | TransportKind)}
        options={[
          { value: 'ALL', label: t('transport:attach.filterAll') },
          ...matchingKinds.map((kind) => ({ value: kind, label: t(transportKindLabelKey(kind)) })),
        ]}
      />
      <Input.Search
        allowClear
        value={search}
        placeholder={t('transport:attach.searchPlaceholder')}
        onChange={(event) => setSearch(event.target.value)}
      />
      {loadFailed ? <Alert type="error" showIcon message={t('transport:orderTransports.loadExistingError')} /> : null}
      {loading ? <Spin size="small" /> : (
        <div className="transport-attach-candidates">
          {candidates.map(({ transport, kind }) => {
            const reason = disabledReason(transport);
            const route = buildTransportRouteLabel(
              transport.loadingAddresses,
              transport.deliveryAddresses,
              transport.originCity,
              transport.destinationCity,
            );
            return (
              <button
                type="button"
                key={transport.id}
                disabled={Boolean(reason)}
                data-testid="transport-attach-candidate"
                className={`transport-attach-candidate${selectedId === transport.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedId(transport.id)}
              >
                <span className="transport-attach-candidate__description">
                  <strong>
                    {[getTransportLabel(transport), route.label, formatDate(transport.loadingDate), transport.transportCompany]
                      .filter((value) => value && value !== '-').join(' · ')}
                  </strong>
                  <small>
                    {[
                      transport.routeOrders.length
                        ? t('transport:attach.customerOrdersCount', { count: transport.routeOrders.length })
                        : null,
                      (transport.supplierOrders || []).length
                        ? t('transport:attach.supplierOrdersCount', { count: transport.supplierOrders.length })
                        : null,
                    ].filter(Boolean).join(' · ') || t('transport:attach.noOrders')}
                  </small>
                  {reason ? <small className="transport-attach-candidate__reason">{reason}</small> : null}
                </span>
                <span className="transport-attach-candidate__badges">
                  <TransportKindTag kind={kind} />
                  <Tag>{t(`transport:status.${transport.status.toLowerCase()}`)}</Tag>
                </span>
              </button>
            );
          })}
          {candidates.length === 0 ? <Text type="secondary">{t('transport:attach.empty')}</Text> : null}
        </div>
      )}
      {selected && newPointMessage(selected) ? (
        <Alert type="info" showIcon data-testid="transport-attach-new-point" message={newPointMessage(selected)} />
      ) : null}
      <Space wrap>
        {onEditInstead ? (
          <Button disabled={!selected} onClick={() => selected && onEditInstead(selected.id)}>
            {t('transport:orderTransports.editCargoAndLink')}
          </Button>
        ) : null}
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          // Round 2 (U8): never an active button that cannot add.
          disabled={!selected || Boolean(disabledReason(selected))}
          loading={saving}
          onClick={() => { void attach(); }}
          data-testid="transport-attach-submit"
        >
          {t('transport:attach.action')}
        </Button>
      </Space>
    </Space>
  );
};

export default TransportAttachCandidates;
